import { IAuthMeV2OutputDto, TypologiesEnum } from '@quadminds/shared/auth-system';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { UserPlatformEnum } from '../enums/platform.enum';

export const isStorkUser = (): boolean => globalThis.localStorage?.getItem('isStorkUser') === 'true';

export const getUserPlatform = (): UserPlatformEnum => {
    if (isStorkUser()) return UserPlatformEnum.STORK;
    return UserPlatformEnum.SAAS;
};

export const isStorkPlatform = (): boolean => getUserPlatform() === UserPlatformEnum.STORK;

const PLATFORMS_WITHOUT_API_KEY: UserPlatformEnum[] = [UserPlatformEnum.STORK];

export const requiresApiKey = (): boolean => !PLATFORMS_WITHOUT_API_KEY.includes(getUserPlatform());

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
}

export function getFileNameFromUrl(url: string): string {
    try {
        const parsedUrl = new URL(url);
        const pathname = parsedUrl.pathname;
        const parts = pathname.split('/').filter(Boolean);
        const lastPart = parts[parts.length - 1];

        // Si termina en .pdf, .docx, etc. => devolver nombre de archivo
        if (/\.[a-zA-Z0-9]+$/.test(lastPart)) {
            return lastPart;
        }

        // Si no hay archivo, generar uno sintético
        return `document_from_${parsedUrl.hostname}.html`;
    } catch (e) {
        console.error('Invalid URL:', url, e);
        return 'invalid_url';
    }
}

export const getFileSize = (file: File): string => {
    const sizeInBytes = file.size;
    const sizeInKB = sizeInBytes / 1024;
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return sizeInMB >= 1 ? `${sizeInMB.toFixed(2)} MB` : `${sizeInKB.toFixed(2)} KB`;
};

/** Roles that grant full admin privileges in Mindy */
const MINDY_ADMIN_ROLES = ['mindy:admin', 'mindy:admin-doc'] as const;

/** Checks if a user's roles array includes any Mindy admin role */
export const hasAdminRole = (roles?: string[]): boolean => {
    return roles?.some((role) => (MINDY_ADMIN_ROLES as readonly string[]).includes(role)) ?? false;
};

/** Checks if a single role name is a Mindy admin role */
export const isAdminRoleName = (roleName: string): boolean => {
    return (MINDY_ADMIN_ROLES as readonly string[]).includes(roleName);
};

export const shouldHideSuggestedQuestions = (userInfo?: IAuthMeV2OutputDto): boolean => {
    const currentOrganization = userInfo?.currentOrganization;
    if (isStorkUser()) return true;

    const showMindySuggestedQuestions =
        currentOrganization && userInfo?.settings?.[currentOrganization]
            ? (userInfo.settings[currentOrganization] as Record<string, boolean>)?.[
                  'mindy:show_mindy_suggested_questions'
              ] ?? true
            : true;
    return userInfo?.typology === TypologiesEnum.SERVICE_STATION || !showMindySuggestedQuestions;
};

export const isKnowledgeUser = (userInfo?: IAuthMeV2OutputDto): boolean =>
    userInfo?.typology === TypologiesEnum.MINDY_CLARO_KNOWLEDGE_USER;

export const getDefaultAgentVersion = (userInfo?: IAuthMeV2OutputDto): string =>
    userInfo?.typology === TypologiesEnum.SERVICE_STATION ? 'v2' : 'v3';
